import React, { useState, useEffect } from 'react';
import { X, Search, Calendar, Loader2, Eye } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import * as API from '../../../api';
import { Modal } from 'react-bootstrap';

type HistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type HistoryDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  data: any;
};

const HistoryDetailsModal = ({ isOpen, onClose, data }: HistoryDetailsModalProps) => {
  if (!isOpen || !data) return null;

  const parsedData = JSON.parse(data.data);

  return (
    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Detalhes da Alteração</h3>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-gray-700">Consultor</h4>
              <p className="text-gray-900">{parsedData.consultant}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-700">E-mail do Consultor</h4>
              <p className="text-gray-900">{parsedData.consultant_mail}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-700">Coordenador</h4>
              <p className="text-gray-900">{parsedData.coordinator}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-700">E-mail do Coordenador</h4>
              <p className="text-gray-900">{parsedData.coordinator_mail}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-700">Gerente</h4>
              <p className="text-gray-900">{parsedData.manager}</p>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Distribuidores</h4>
            <div className="space-y-1">
              {parsedData.distributors.map((dist: any, index: number) => (
                <p key={index} className="text-gray-900">{dist.label}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const HistoryModal = ({ isOpen, onClose }: HistoryModalProps) => {
  const [history, setHistory] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const [selectedItem, setSelectedItem] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
  }, [isOpen]);

  const loadHistory = async () => {
    setIsLoading(true);
    try {
      const response = await API.getConsultantsHistory();
      setHistory(response.data.reverse());
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getType = (logType: string) => {
    if (logType === "unbilled-orders-add") return "Novo Registro";
    if (logType === "unbilled-orders-remove") return "Registro Removido";
    if (logType === "unbilled-orders-update") return "Registro Alterado";
    return "";
  };

  const getColorByType = (logType: string) => {
    if (logType === "unbilled-orders-add") return "green";
    if (logType === "unbilled-orders-remove") return "red";
    if (logType === "unbilled-orders-update") return "orange";
    return "gray";
  };

  const filteredHistory = history.filter(item => {
    const matchesSearch = searchTerm
      ? item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    const itemDate = new Date(item.created_at);
    const matchesDateRange = startDate && endDate
      ? itemDate >= startDate && itemDate <= endDate
      : true;

    return matchesSearch && matchesDateRange;
  });

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-xl max-w-5xl w-full overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">
              Histórico de Alterações
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="text-gray-400 w-5 h-5 flex-shrink-0" />
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => setDateRange(update)}
                  placeholderText="Filtrar por período..."
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500"
                  dateFormat="dd/MM/yyyy"
                  isClearable
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="max-h-[40rem] overflow-y-auto space-y-6">
              {/* max-h-96 = 384px, ajuste conforme o tamanho do card (ou use max-h-[28rem] para 448px) */}
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  <p className="mt-4 text-gray-500">Carregando histórico...</p>
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  Nenhum registro encontrado
                </div>
              ) : (
                filteredHistory.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900">{item.name} - {item.email}</h4>
                        <p className={`font-medium text-${getColorByType(item.log_name)}-700 mt-1`}>
                          {getType(item.log_name)}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="text-sm text-gray-500">
                          {new Date(item.created_at).toLocaleString()}
                        </span>
                        { item.log_name !== 'unbilled-orders-remove' ? <button
                          onClick={() => setSelectedItem(item)}
                          className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Ver detalhes"
                        >
                          <Eye className="w-5 h-5" />
                        </button> : null }
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <HistoryDetailsModal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        data={selectedItem}
      />
    </>
  );
};